import { SortDirection } from '#Common/models'
import { WellKnownAttribute } from '#Products/models/Pim/Enums/WellKnownAttribute'

export type ListingSortValue = {
  sortField: string
  sortDirection: SortDirection
}

export const LISTING_SORT_CARRY_PARAMS = ['q', 'sort', 'dir'] as const

const DEFAULT_SORT: ListingSortValue = {
  sortField: 'manual',
  sortDirection: SortDirection.ASC,
}

/**
 * Friendly URL aliases for CatalogShowcase sort (S20).
 * `?sort=manual|price|updated&dir=asc|desc` — kept out of canonical.
 */
export function listingSortFromQuery(
  query: Record<string, unknown>,
  wellKnown: Partial<Record<WellKnownAttribute, string>>,
): ListingSortValue {
  const sort = String(query.sort ?? '').trim()
  const dir
    = String(query.dir ?? '').trim() === 'desc'
      ? SortDirection.DESC
      : SortDirection.ASC

  if (sort === 'updated') {
    return { sortField: 'updated', sortDirection: dir }
  }

  if (sort === 'price') {
    const priceField = wellKnown[WellKnownAttribute.Price]
    if (priceField) {
      return { sortField: priceField, sortDirection: dir }
    }
  }

  return { ...DEFAULT_SORT }
}

export function listingSortToQuery(
  value: ListingSortValue,
  wellKnown: Partial<Record<WellKnownAttribute, string>>,
): { sort?: string, dir?: string } {
  if (value.sortField === 'manual') {
    return {}
  }

  if (value.sortField === 'updated') {
    return {
      sort: 'updated',
      dir: value.sortDirection === SortDirection.DESC ? 'desc' : 'asc',
    }
  }

  const priceField = wellKnown[WellKnownAttribute.Price]
  if (priceField && value.sortField === priceField) {
    return {
      sort: 'price',
      dir: value.sortDirection === SortDirection.DESC ? 'desc' : 'asc',
    }
  }

  return {}
}

/** Non-pagination query keys to keep when clearing cursor params. */
export function pickListingCarryQuery(
  query: Record<string, unknown>,
  extra: Record<string, string | undefined> = {},
): Record<string, string> {
  const next: Record<string, string> = {}

  for (const name of LISTING_SORT_CARRY_PARAMS) {
    const fromRoute = String(query[name] ?? '').trim()
    const override = extra[name]
    const value = override !== undefined ? String(override).trim() : fromRoute
    if (value) next[name] = value
  }

  return next
}
